import { ConfigStateService } from '@abp/ng.core';
import { getLocaleCurrencySymbol } from '@angular/common';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Input,
  OnInit,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';

@Directive({
  selector: '[input[autoMask], textarea[autoMask]]',
  standalone: true,
  hostDirectives: [NgxMaskDirective],
})
export class AutoMaskDirective extends NgxMaskDirective implements OnInit {
  @Input('autoMask') type: AutoMaskType;

  constructor(
    private elementRef: ElementRef<HTMLDivElement | HTMLTextAreaElement>,
    private ngControl: NgControl,
    private cdr: ChangeDetectorRef,
    private configStateService: ConfigStateService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.type != null) {
      let simpleChangeList = new Array<Record<string, SimpleChange>>();
      let cultureName = this.configStateService.getDeep(
        LOCALIZATION__CURRENT_CULTURE__CULTURE_NAME,
      ) as string;
      switch (this.type) {
        case 'integer':
          simpleChangeList.push(this.defineMask('separator.0'));
          break;
        case 'float':
          simpleChangeList.push(
            this.defineDecimalMarker(this.getDecimalMarker(cultureName)),
            this.defineMask('separator'),
          );
          break;
        case 'currency':
          simpleChangeList.push(
            this.defineSuffix(this.getCurrencySymbol(cultureName)),
            this.defineDecimalMarker(this.getDecimalMarker(cultureName)),
            this.defineLeadZero(true),
            this.defineMask('separator.2'),
          );
          break;
        case 'percentage':
          simpleChangeList.push(
            this.defineSuffix(' %'),
            this.defineDecimalMarker(this.getDecimalMarker(cultureName)),
            this.defineMask('percent.2'),
          );
          break;
      }
      this.applyMask(simpleChangeList);
    }
  }

  private applyMask(simpleChangeList: Record<string, SimpleChange>[]) {
    if (simpleChangeList.length > 0) {
      let simpleChanges: SimpleChanges = simpleChangeList.reduce((changes, simpleChange) => {
        return Object.assign(changes, simpleChange);
      }, {});
      this.ngOnChanges(simpleChanges);

      this.writeValue(this.ngControl.control.value);
      //If form got resetted
      this.ngControl.control.valueChanges.subscribe(v => {
        if (this.elementRef.nativeElement != document.activeElement) {
          this.writeValue(v);
        }
      });
      this.cdr.detectChanges();
    }
  }

  private defineSuffix(suffix: string): Record<string, SimpleChange> {
    let simpleChange = { suffix: new SimpleChange(this.suffix, suffix, true) };
    this.suffix = suffix;
    return simpleChange;
  }

  private defineDecimalMarker(decimalMarker: ',' | '.' | ['.', ',']): Record<string, SimpleChange> {
    let simpleChange = { decimalMarker: new SimpleChange(this.decimalMarker, decimalMarker, true) };
    this.decimalMarker = decimalMarker;
    return simpleChange;
  }

  private defineMask(maskExpression: string): Record<string, SimpleChange> {
    let simpleChange = {
      maskExpression: new SimpleChange(this.maskExpression, maskExpression, true),
    };
    this.maskExpression = maskExpression;
    return simpleChange;
  }

  private defineLeadZero(leadZero: boolean): Record<string, SimpleChange> {
    let simpleChange = { leadZero: new SimpleChange(this.leadZero, leadZero, true) };
    this.leadZero = leadZero;
    return simpleChange;
  }

  private getDecimalMarker(cultureName: string): ',' | '.' {
    let formatter = new Intl.NumberFormat(cultureName);
    let parts = formatter.formatToParts(1.1);
    return (parts.find(part => part.type === 'decimal')?.value as ',' | '.') ?? '.';
  }

  private getCurrencySymbol(cultureName: string) {
    return ' ' + (getLocaleCurrencySymbol(cultureName) ?? '€');
  }
}

export const LOCALIZATION__CURRENT_CULTURE__CULTURE_NAME =
  'localization.currentCulture.cultureName';

type AutoMaskType = 'integer' | 'float' | 'currency' | 'percentage';
